export interface Task {
  id: string;
  status: 'incomplete' | 'complete' | 'canceled';
  title: string;
  notes?: string;
}

export interface ParsedContent {
  type: 'text' | 'tasks';
  content: string | Task[];
  title?: string;
}

/**
 * Parses content containing <task> blocks and returns structured data
 * @param content The raw content potentially containing <task> blocks
 * @returns Array of parsed content blocks
 */
export function parseTaskBlocks(content: string): ParsedContent[] {
  const taskBlockRegex = /<task>([\s\S]*?)<\/task>/g;
  const result: ParsedContent[] = [];
  let lastIndex = 0;
  let match;

  // Check if there's a title line before tasks (e.g., "Incomplete Tasks (3):")
  const lines = content.split('\n');
  let titleLine = '';
  let contentStartIndex = 0;

  // Look for a title pattern like "Title (count):"
  if (lines.length > 0 && lines[0].match(/^[^<]*\(\d+\):$/)) {
    titleLine = lines[0];
    contentStartIndex = lines[0].length + 1; // +1 for newline
    
    // Skip empty line after title if present
    if (lines.length > 1 && lines[1].trim() === '') {
      contentStartIndex += lines[1].length + 1;
    }
  }

  const contentToParse = content.substring(contentStartIndex);
  const tasks: Task[] = [];

  while ((match = taskBlockRegex.exec(contentToParse)) !== null) {
    // Add any text before this task block
    if (match.index > lastIndex) {
      const textBefore = contentToParse.substring(lastIndex, match.index).trim();
      if (textBefore && !titleLine) {
        result.push({
          type: 'text',
          content: textBefore
        });
      }
    }

    // Parse the task block
    const taskContent = match[1].trim();
    const task = parseTask(taskContent);
    if (task) {
      tasks.push(task);
    }

    lastIndex = match.index + match[0].length;
  }

  // If we found tasks, add them as a single tasks block
  if (tasks.length > 0) {
    result.push({
      type: 'tasks',
      content: tasks,
      title: titleLine || undefined
    });
  }

  // Add any remaining text after the last task block
  if (lastIndex < contentToParse.length) {
    const remainingText = contentToParse.substring(lastIndex).trim();
    if (remainingText) {
      result.push({
        type: 'text',
        content: remainingText
      });
    }
  }

  // If no task blocks were found, return the original content as text
  if (result.length === 0 && content.trim()) {
    result.push({
      type: 'text',
      content: content
    });
  }

  return result;
}

/**
 * Parses a single task block content
 * @param taskContent The content inside <task> tags
 * @returns Parsed task object or null if invalid
 */
function parseTask(taskContent: string): Task | null {
  const lines = taskContent.split('\n').map(line => line.trim());
  const task: Partial<Task> = {};
  let notesStartIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('ID:')) {
      task.id = line.substring(3).trim();
    } else if (line.startsWith('Status:')) {
      const status = line.substring(7).trim().toLowerCase();
      if (status === 'incomplete' || status === 'complete' || status === 'canceled') {
        task.status = status;
      }
    } else if (line.startsWith('Title:')) {
      task.title = line.substring(6).trim();
    } else if (line === '' && i > 0 && notesStartIndex === -1) {
      // Empty line indicates notes section starts next
      notesStartIndex = i + 1;
      break;
    }
  }

  // Extract notes if present
  if (notesStartIndex > 0 && notesStartIndex < lines.length) {
    const notesLines = lines.slice(notesStartIndex).filter(line => line !== '');
    if (notesLines.length > 0) {
      task.notes = notesLines.join('\n');
    }
  }

  // Validate required fields
  if (task.id && task.status && task.title) {
    return task as Task;
  }

  return null;
}